import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastService } from '@peca/commons';
import { SellerService } from '../../../sellers/services/seller.service';
import { SalesChannelsService } from '../../../sales-channels/services/sales-channels.service';
import { EMPTY, Subject, takeUntil, concat, expand, reduce, toArray } from 'rxjs';
import {
  MultiSelectAutocompleteItemSource,
  MultiSelectAutocompleteItem,
} from 'libs/design-system/src/lib/components/multi-select-autocomplete/multi-select-autocomplete.model';
import { ConfirmableActionService } from '../../../../core/services/confirmable-action.service';

import { CommercialRestrictService } from '../../services/commercial-restrict.service';
import { CreateBranchRestrictionDto } from '../../types/commercial-restrict.type';
import { PaginationInitialState, PaginationInitialStateV2 } from '@peca/design-system';

type Mode = 'create' | 'edit';

@Component({
  selector: 'peca-commercial-restrict-form',
  templateUrl: './commercial-restrict-form.component.html',
  styleUrls: ['./commercial-restrict-form.component.scss'],
  standalone: false,
})
export class CommercialRestrictFormComponent implements OnInit, OnDestroy {
  private readonly service = inject(CommercialRestrictService);
  private readonly toast = inject(ToastService);
  private readonly sellerService = inject(SellerService);
  private readonly salesChannelsService = inject(SalesChannelsService);
  private readonly confirmable = inject(ConfirmableActionService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  private destroy$ = new Subject<void>();

  mode: Mode = 'create';
  loading = false;
  isEditing = false;

  readonly form = new FormGroup({
    id: new FormControl<string | null>(null),
    salesChannels: new FormControl<any[]>([], [Validators.required]),
    sellers: new FormControl<any[]>([], [Validators.required]),
    enabled: new FormControl(true),
  });

  salesChannelsSuggestions: MultiSelectAutocompleteItemSource = new MultiSelectAutocompleteItemSource();
  sellerSuggestions: MultiSelectAutocompleteItemSource = new MultiSelectAutocompleteItemSource();

  branchesBySeller: Record<string, Array<{ id: string; label: string }>> = {};
  selectedBranches: Record<string, Set<string>> = {};

  ngOnInit() {
    this.form
      .get('sellers')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((selected: any[] | null) => {
        this.loadBranchesForSelectedSellers(selected ?? []);
      });

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.openDetailsPage(id);
      } else {
        this.initializeCreateForm();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get title() {
    return this.mode === 'create' ? 'Nova restrição de vendedores' : 'Editar restrição';
  }

  private initializeCreateForm() {
    this.mode = 'create';
    this.isEditing = true;
    this.loading = false;
    this.form.enable();
    this.form.reset({ id: null, salesChannels: [], sellers: [], enabled: true });
    this.branchesBySeller = {};
    this.selectedBranches = {};
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private openDetailsPage(id: string) {
    this.mode = 'edit';
    this.isEditing = false;
    this.loading = true;
    this.form.enable();

    this.service.getById(id).subscribe({
      next: (r) => {
        const sellers = r.sellers.map((seller: any) => ({
          id: `${seller.id}`,
          label: seller.name,
        }));

        const selectedBranches: Record<string, Set<string>> = {};

        for (const seller of r.sellers) {
          this.branchesBySeller[`${seller.id}`] = seller.branches.map((branch: any) => ({
            id: branch.name,
            label: branch.name,
          }));

          selectedBranches[`${seller.id}`] = new Set(seller.branches.filter((branch: any) => branch.restricted).map((branch: any) => branch.name));
        }

        this.selectedBranches = selectedBranches;

        this.form.patchValue({
          id: r.id,
          salesChannels: r.salesChannels.map((s: any) => ({ id: s.id, label: s.name })),
          sellers: sellers,
          enabled: r.enabled,
        });

        this.form.disable();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toast.failure('Não foi possível carregar os detalhes.');
        this.navigateBack();
      },
    });
  }

  navigateBack() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  onClickSave() {
    if ((!this.isEditing && this.mode === 'edit') || this.form.invalid || this.loading) {
      if (this.mode === 'edit' && !this.isEditing) {
        return;
      }
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;
    this.form.disable();

    const payload = this.buildPayload();
    const raw = this.form.getRawValue();

    const request$ = this.mode === 'edit' && raw.id ? this.service.update(raw.id, payload) : this.service.create(payload);

    request$.subscribe({
      next: () => {
        this.loading = false;
        this.toast.success(this.mode === 'edit' ? 'Restrição atualizada com sucesso.' : 'Restrição cadastrada com sucesso.');
        this.navigateBack();
      },
      error: (error: any) => {
        this.loading = false;
        this.form.enable();

        if (error?.status === 409) {
          this.toast.failure('Essa restrição já está cadastrada.');
          return;
        }

        this.toast.failure(this.mode === 'edit' ? 'Falha ao atualizar restrição.' : 'Falha ao cadastrar restrição.');
      },
    });
  }

  onClickDeactivate() {
    if (this.loading) return;

    const raw = this.form.getRawValue();
    if (!raw.id) return;

    const nextEnabled = !raw.enabled;
    const action = nextEnabled ? 'ativar' : 'desativar';

    const restrictionId = raw.id;
    if (!restrictionId) return;

    this.confirmable.confirm(`Tem certeza que deseja ${action} esta restrição?`, restrictionId).subscribe((response) => {
      if (!response.confirmed) return;

      this.loading = true;
      this.form.disable();

      const payload = this.buildPayload();
      payload.enabled = nextEnabled;

      this.service.update(restrictionId, payload).subscribe({
        next: () => {
          this.loading = false;
          this.toast.success(nextEnabled ? 'Restrição ativada com sucesso.' : 'Restrição desativada com sucesso.');
          this.navigateBack();
        },
        error: () => {
          this.loading = false;
          this.form.enable();
          this.toast.failure('Falha ao atualizar o status da restrição.');
        },
      });
    });
  }

  onClickEdit() {
    if (this.loading) return;
    this.isEditing = true;
    this.form.enable();
  }

  onClickDelete() {
    if (this.loading) return;

    const raw = this.form.getRawValue();
    if (!raw.id) return;

    const restrictionId = raw.id;
    if (!restrictionId) return;

    this.confirmable.confirm('Tem certeza que deseja excluir esta restrição? Esta ação não pode ser desfeita.', restrictionId, true).subscribe((response) => {
      if (!response.confirmed) return;

      this.loading = true;
      this.form.disable();

      this.service.remove(restrictionId).subscribe({
        next: () => {
          this.loading = false;
          this.toast.success('Restrição excluída com sucesso.');
          this.navigateBack();
        },
        error: () => {
          this.loading = false;
          this.form.enable();
          this.toast.failure('Falha ao excluir a restrição.');
        },
      });
    });
  }

  onSalesChannelsAutocomplete(search: string) {
    if (!search || search.length < 1) return;

    this.salesChannelsService.suggestSalesChannels(search).subscribe({
      next: (items) => {
        this.salesChannelsSuggestions = new MultiSelectAutocompleteItemSource(items as MultiSelectAutocompleteItem[], false, false);
      },
      error: () => (this.salesChannelsSuggestions = new MultiSelectAutocompleteItemSource()),
    });
  }

  onSellerAutocomplete(search: string) {
    if (!search || search.length < 1) return;

    this.sellerService.suggestSellers(search).subscribe({
      next: (items) => {
        const mapped = (items || []).map((i: any) => ({ id: `${i.id}`, label: i.label, selected: false } as MultiSelectAutocompleteItem));
        this.sellerSuggestions = new MultiSelectAutocompleteItemSource(mapped, false, false);
      },
      error: () => (this.sellerSuggestions = new MultiSelectAutocompleteItemSource()),
    });
  }

  private loadBranchesForSelectedSellers(selected: any[]) {
    const ids = (selected || []).map((s: any) => s.id).filter(Boolean);
    Object.keys(this.branchesBySeller).forEach((k) => {
      if (!ids.includes(k)) {
        delete this.branchesBySeller[k];
        delete this.selectedBranches[k];
      }
    });

    const calls = ids
      .filter((id) => !this.branchesBySeller[id])
      .map((id) =>
        this.sellerService
          .fetchSellerBranches(id, { ...PaginationInitialStateV2, size: 36 })
          .pipe(
            expand((response) =>
              response.currentPage < response.totalPages
                ? this.sellerService.fetchSellerBranches(id, {
                  ...PaginationInitialStateV2,
                  size: 36,
                  page: response.currentPage + 1,
                })
                : EMPTY
            ),
            reduce((items, response) => [...items, ...response.items], [] as any[]),
            takeUntil(this.destroy$)
          )
      );

    if (!calls.length) return;

    concat(...calls)
      .pipe(toArray())
      .subscribe({
        next: (results: any[]) => {
          let i = 0;
          ids.forEach((id) => {
            if (!this.branchesBySeller[id]) {
              const branches = results[i++] || [];
              this.branchesBySeller[id] = branches.map((b: any) => ({ id: `${b.externalReference}`, label: `${b.tradingName} (${b.externalReference})` }));
              if (!this.selectedBranches[id]) this.selectedBranches[id] = new Set<string>();
            }
          });
        },
        error: () => {
          this.toast.failure('Não foi possível carregar as filiais dos vendedores selecionados.');
          this.navigateBack();
        },
      });
  }

  toggleBranchSelection(sellerId: string, branchId: string, checked: boolean) {
    const set = this.selectedBranches[sellerId] || new Set<string>();
    if (checked) set.add(branchId);
    else set.delete(branchId);
    this.selectedBranches[sellerId] = set;
  }

  onBranchCheckboxChange(sellerId: string, branchId: string) {
    const isSelected = this.selectedBranches[sellerId]?.has(branchId) ?? false;
    this.toggleBranchSelection(sellerId, branchId, !isSelected);
  }

  private buildPayload(): CreateBranchRestrictionDto {
    const raw = this.form.getRawValue();
    const salesChannels = (raw.salesChannels || []).map((s: any) => (s.id != null ? s.id : s).toString());

    const sellersObj: { [key: string]: string[] } = {};
    const sellers = raw.sellers || [];
    sellers.forEach((s: any) => {
      const id = s.id?.toString() ?? s.toString();
      const set = this.selectedBranches[id];
      if (!set || set.size === 0) {
        sellersObj[s.label] = [];
      } else {
        sellersObj[s.label] = Array.from(set.values());
      }
    });

    return {
      salesChannels,
      sellers: sellersObj,
      enabled: raw.enabled ?? true,
    };
  }

  showFieldError(fieldName: 'salesChannels' | 'sellers') {
    const field = this.form.get(fieldName);
    return !!field && field.invalid && (field.dirty || field.touched);
  }
}
